// src/App.jsx
import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import TopBanner from "./components/TopBanner";
import HeroKycIntro from "./components/HeroKycIntro";
import InfoCards from "./components/InfoCards";
import PricingSection from "./components/PricingSection";
import Footer from "./components/Footer";
import Disclaimer from "./components/Disclaimer";
import KycForm from "./components/KycForm";
import Confirmation from "./components/Confirmation";
import Login from "./components/Login";
import VideoLogin from "./components/VideoLogin";
import Dashboard from "./components/Dashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import Contact from "./components/Contact";

const isVideoAuthenticated = () => {
  try {
    return sessionStorage.getItem("isAuthenticated") === "true";
  } catch {
    return false;
  }
};

const RequireVideoAuth = ({ children }) =>
  isVideoAuthenticated() ? children : <Navigate to="/video-login" replace />;

// Routes where footer appears
const FOOTER_ROUTES = ["/home", "/privacy-policy", "/terms", "/contact"];

// Routes where TopBanner is hidden
const HIDE_BANNER_ROUTES = ["/login", "/dashboard"];

// Routes that use the navy KYC theme
const THEME_ROUTES = ["/home", "/kyc-form", "/confirmation", "/video-login", "/privacy-policy", "/terms", "/contact"];

const AppContent = () => {
  const location = useLocation();
  const hideBanner = HIDE_BANNER_ROUTES.includes(location.pathname);
  const showFooter = FOOTER_ROUTES.includes(location.pathname);
  const isThemeRoute = THEME_ROUTES.includes(location.pathname);

  const wrapperStyle = isThemeRoute
    ? {
        background: "linear-gradient(160deg, #071520 0%, #0a1e2e 35%, #071018 100%)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }
    : {
        backgroundImage: `url(${process.env.PUBLIC_URL}/MainBody04.png)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      };

  return (
    <div className="min-h-screen flex flex-col" style={wrapperStyle}>
      {!hideBanner && <TopBanner />}

      <div style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/video-login" replace />} />

          {/* ── HOMEPAGE: TopBanner + HeroKycIntro + InfoCards + PricingSection + Footer ── */}
          <Route
            path="/home"
            element={
              <RequireVideoAuth>
                <>
                  <HeroKycIntro />
                  <InfoCards />
                  <PricingSection />
                </>
              </RequireVideoAuth>
            }
          />

          <Route path="/disclaimer" element={<Disclaimer />} />

          <Route
            path="/kyc-form"
            element={
              <RequireVideoAuth>
                <KycForm />
              </RequireVideoAuth>
            }
          />

          <Route path="/video-login" element={<VideoLogin />} />
          <Route path="/login" element={<Login />} />

          <Route
            path="/confirmation"
            element={
              <RequireVideoAuth>
                <Confirmation />
              </RequireVideoAuth>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Legal & Contact pages — publicly accessible (no auth required) */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="*" element={<Navigate to="/video-login" replace />} />
        </Routes>
      </div>

      {showFooter && <Footer />}
    </div>
  );
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
